import * as Sentry from "@sentry/react";
import { setSmallScreenModalOpen } from "src/store/slices/HomeSlice";

export const getCalculatedIndices = (placeholders) => {
  var calculatedIndices = {};
  placeholders.forEach((placeholder) => {
    placeholder["indices"].forEach((index) => {
      calculatedIndices[index.start_index] = {
        startIndex: index.start_index,
        endIndex: index.end_index,
        type: "placeholder",
        placeholder: placeholder,
      };
    });
  });
  return calculatedIndices;
};

export const setBackgroundColors = (quillRef, placeholders) => {
  for (var i = 0; i < placeholders.length; i++) {
    placeholders[i]["indices"].forEach((index) => {
      quillRef?.current?.editor.formatText(index.start_index, index.end_index - index.start_index, {
        // unbolds 'hello' and set its color to blue
        "background-color": "rgba(198, 29, 202, 0.2)",
      });
    });
  }
};

export const removeBackgroundColors = (quillRef, placeholders) => {
  for (var i = 0; i < placeholders.length; i++) {
    placeholders[i]["indices"].forEach((index) => {
      quillRef?.current?.editor.formatText(index.start_index, index.end_index - index.start_index, {
        // unbolds 'hello' and set its color to blue
        "background-color": "transparent",
      });
    });
  }
};

export const setUpDocument = (data, Delta) => {
  var defaultData = new Delta();
  // insert text and attributes
  for (var i = 0; i < data.ops.length; i++) {
    if (data.ops[i].insert) {
      defaultData.insert(data.ops[i].insert, data.ops[i].attributes);
    }
  }
  return defaultData;
};

export const updatePlaceholderValue = (quillRef, placeholders, placeholderIndex, updatedText, setPlaceholders) => {
  var initialValue = placeholders[placeholderIndex]["current_value"];
  placeholders[placeholderIndex]["current_value"] = updatedText;
  var indices = placeholders[placeholderIndex]["indices"];
  var stringLength = updatedText.length - initialValue.length;
  if (stringLength < 0) {
    stringLength = 0;
  }
  var forward = 0;
  var newIndices = [];
  indices.forEach((index) => {
    quillRef?.current?.editor.formatText(index.start_index + forward, forward, {
      // unbolds 'hello' and set its color to blue
      "background-color": "transparent",
    });

    quillRef?.current?.editor.deleteText(index.start_index + forward, index.end_index - index.start_index);
    quillRef?.current?.editor.insertText(index.start_index + forward, updatedText);
    quillRef?.current?.editor.formatText(index.start_index + forward, index.end_index - index.start_index, {
      // unbolds 'hello' and set its color to blue
      "background-color": "transparent",
    });
    // quillRef?.current?.editor.formatText(index.start_index + forward, updatedText.length, {                   // unbolds 'hello' and set its color to blue
    //     'background-color': 'rgba(148, 29, 202, 0.2)'
    // });

    // I think the following might not be required if we are going to get the updated indices from the backend
    var new_start_index = index.start_index + forward;
    var new_end_index = index.start_index + updatedText.length + forward;
    newIndices.push({ start_index: new_start_index, end_index: new_end_index });
    forward += stringLength;
  });
  // I think the following might not be required if we are going to get the updated indices from the backend
  // we might have to update the placeholders data with the updated text though
  placeholders[placeholderIndex]["indices"] = newIndices;
  setPlaceholders([...placeholders]);
};

class CustomAppError extends Error {
  constructor(name, message) {
    super(message); // sets the message
    this.name = name; // sets the error type/title
  }
}

class WebsocketDisconnectedError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

class FailedToCreateThreadError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

class FailedToGenerateDraftError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

class FailedToSendMessageError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

class InvalidThreadOwnerError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

class SegmentIdNotFoundError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

class JsonParsingError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

class FailedToResetAnalyticsError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

class WebsocketMessageError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

class FailedToSendFeedbackError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

class SubmitLegalReviewError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

class ConvertDocumentError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

class GetRetainerError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

class GetSharedThreadError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

class AddLegalReviewFeedbackError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

class GetCheckoutUrlError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

class GetPaymentStatusError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

class FailedToGetThreadMessagesError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

/**
 * Used in messageSlice.js - Case: FETCH_MESSAGE_HISTORY_FAILED
 * File: src/store/slices/messageSlice.js
 */
class FailedToGetPresignedUrl extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

/**
 * Used in messageSlice.js - Case: EDIT_MESSAGE_FAILED
 * File: src/store/slices/messageSlice.js
 */
class ProcessAttachmentError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

/**
 * Used in messageSlice.js - Case: REGENERATE_AI_RESPONSE_FAILED
 * File: src/store/slices/messageSlice.js
 */
class UploadFileError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

/**
 * Used in messageSlice.js - Case: SHARE_THREAD_FAILED
 * File: src/store/slices/messageSlice.js
 */
class FailedToGetSharedMessagesError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

// Error classes for each case where ErrorLogging is called
class GetSavedPartiesError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

class GetAllDraftTypesError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

class GetEducationMetadataError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

class CreateNewPartyError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

class OnboardUserError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

class GetAllThreadsError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

class GetTitleError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

class GenerateSharedIdError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

class GenerateDownloadDraftUrlError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

class DeleteThreadError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

class UpdateTitleError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

class GetUnreadThreadsError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

class MarkThreadReadError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

class GenerateReferenceDocDownloadUrlError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

// Error classes for all error cases in onboardingSlice.js

class CreateUserAPIError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

class GetUserMetadataError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

class GetLawyerHubError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

class MarkLegalReviewCallScheduledError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

class GetMessageTypeError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

class RequestLegalReviewError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

class FetchMeetingDetailsError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

class StoreReferralError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

class WebsocketMonitoringError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}
class WebsocketClosedError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

class BackendWSError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

class GptThreadCreateRequestError extends CustomAppError {
  constructor(name, message) {
    super(name, message);
    this.name = name;
  }
}

// Base class definition (if not already defined elsewhere)

const getErrorObjectAndFingerPrint = (category, originalDescription) => {
  var error, fingerprint;
  var description = JSON.stringify(originalDescription);
  var title = `Error: ${category}`;
  switch (category) {
    case "websocket_disconnected":
      error = new WebsocketDisconnectedError(title, description);
      fingerprint = "websocket";
      break;
    case "backend_ws_error":
      error = new BackendWSError(title, description);
      fingerprint = "websocket";
      break;
    case "failed_to_create_thread":
      error = new FailedToCreateThreadError(title, description);
      fingerprint = "websocket";
      break;
    case "failed_to_generate_draft":
      error = new FailedToGenerateDraftError(title, description);
      fingerprint = "websocket";
      break;
    case "failed_to_send_message":
      error = new FailedToSendMessageError(title, description);
      fingerprint = "websocket";
      break;
    case "invalid_thread_owner":
      error = new InvalidThreadOwnerError(title, description);
      fingerprint = "websocket";
      break;
    case "segment_id_not_found":
      error = new SegmentIdNotFoundError(title, description);
      fingerprint = "segment";
      break;
    case "json_parsing_error":
      error = new JsonParsingError(title, description);
      fingerprint = null;
      break;
    case "failed_to_reset_analytics":
      error = new FailedToResetAnalyticsError(title, description);
      fingerprint = "segment";
      break;
    case "websocket_message_error":
      title = `${title}:${originalDescription?.description?.action || ""}`;
      error = new WebsocketMessageError(title, description);
      fingerprint = "websocket";
      break;
    case "failed_to_send_feedback":
      error = new FailedToSendFeedbackError(title, description);
      fingerprint = "websocket";
      break;
    case "submit_legal_review_error":
      error = new SubmitLegalReviewError(title, description);
      fingerprint = "backend";
      break;
    case "convert_document_error":
      error = new ConvertDocumentError(title, description);
      fingerprint = "backend";
      break;
    case "get_retainer_error":
      error = new GetRetainerError(title, description);
      fingerprint = "backend";
      break;
    case "get_shared_thread_error":
      error = new GetSharedThreadError(title, description);
      fingerprint = "backend";
      break;
    case "add_legal_review_feedback_error":
      error = new AddLegalReviewFeedbackError(title, description);
      fingerprint = "backend";
      break;
    case "get_checkout_url_error":
      error = new GetCheckoutUrlError(title, description);
      fingerprint = "backend";
      break;
    case "get_payment_status_error":
      error = new GetPaymentStatusError(title, description);
      fingerprint = "backend";
      break;
    case "failed_to_get_thread_messages":
      error = new FailedToGetThreadMessagesError(title, description);
      fingerprint = "backend";
      break;
    case "failed_to_get_presigned_url":
      error = new FailedToGetPresignedUrl(title, description);
      fingerprint = "backend";
      break;
    case "update_uploaded_file_name_error":
      error = new ProcessAttachmentError(title, description);
      fingerprint = "backend";
      break;
    case "upload_file_error":
      error = new UploadFileError(title, description);
      fingerprint = "backend";
      break;
    case "failed_to_get_shared_messages":
      error = new FailedToGetSharedMessagesError(title, description);
      fingerprint = "backend";
      break;
    case "get_saved_parties_error":
      error = new GetSavedPartiesError(title, description);
      fingerprint = "backend";
      break;
    case "get_all_draft_types_error":
      error = new GetAllDraftTypesError(title, description);
      fingerprint = "backend";
      break;
    case "get_education_metadata_error":
      error = new GetEducationMetadataError(title, description);
      fingerprint = "backend";
      break;
    case "create_new_party_error":
      error = new CreateNewPartyError(title, description);
      fingerprint = "backend";
      break;
    case "onboard_user_error":
      error = new OnboardUserError(title, description);
      fingerprint = "backend";
      break;
    case "get_all_threads_error":
      error = new GetAllThreadsError(title, description);
      fingerprint = "backend";
      break;
    case "get_title_error":
      error = new GetTitleError(title, description);
      fingerprint = "backend";
      break;
    case "generate_shared_id_error":
      error = new GenerateSharedIdError(title, description);
      fingerprint = "backend";
      break;
    case "generate_download_draft_url_error":
      error = new GenerateDownloadDraftUrlError(title, description);
      fingerprint = "backend";
      break;
    case "delete_thread_error":
      error = new DeleteThreadError(title, description);
      fingerprint = "backend";
      break;
    case "update_title_error":
      error = new UpdateTitleError(title, description);
      fingerprint = "backend";
      break;
    case "get_unread_threads_error":
      error = new GetUnreadThreadsError(title, description);
      fingerprint = "backend";
      break;
    case "mark_thread_read_error":
      error = new MarkThreadReadError(title, description);
      fingerprint = "backend";
      break;
    case "generate_reference_doc_download_url_error":
      error = new GenerateReferenceDocDownloadUrlError(title, description);
      fingerprint = "backend";
      break;
    case "create_user_api_error":
      error = new CreateUserAPIError(title, description);
      fingerprint = "backend";
      break;
    case "get_user_metadata_error":
      error = new GetUserMetadataError(title, description);
      fingerprint = "backend";
      break;
    case "get_lawyer_hub_error":
      error = new GetLawyerHubError(title, description);
      fingerprint = "backend";
      break;
    case "mark_legal_review_call_scheduled_error":
      error = new MarkLegalReviewCallScheduledError(title, description);
      fingerprint = "backend";
      break;
    case "get_message_type_error":
      error = new GetMessageTypeError(title, description);
      fingerprint = "backend";
      break;
    case "request_legal_review_error":
      error = new RequestLegalReviewError(title, description);
      fingerprint = "backend";
      break;
    case "fetch_meeting_details_error":
      error = new FetchMeetingDetailsError(title, description);
      fingerprint = "backend";
      break;
    case "store_referral_error":
      error = new StoreReferralError(title, description);
      fingerprint = "backend";
      break;
    case "websocket_closed":
      title = `Websocket closed for ${originalDescription?.description?.duration} seconds`;
      error = new WebsocketClosedError(title, description);
      break;

    case "websocket_monitoring_error":
      title = `${originalDescription?.description?.name}`;
      error = new WebsocketMonitoringError(title, JSON.stringify(originalDescription?.description?.wsMessage));
      break;
    case "gpt-thread-create-request-error":
      error = new GptThreadCreateRequestError(title, description);
      break;
    default:
      error = new CustomAppError(title, description);
      fingerprint = null;
      break;
  }
  return { error: error, fingerprint: fingerprint };
};

export const ErrorLoggingV2 = (description, category) => {
  if (!description) {
    description = `Error occured on ${window.location.href}. category: ${category}`;
  }
  var response = getErrorObjectAndFingerPrint(category, description);
  var error = response["error"];
  const email = localStorage.getItem("email");

  // check the following issue to see how everything is rendered
  // https://round-table-legal-ai-inc.sentry.io/issues/6524240647/?project=4507499547721728&query=is%3Aunresolved%20issue.priority%3A%5Bhigh%2C%20medium%5D&referrer=issue-stream&stream_index=1

  // scope.setExtra("user metadata", userMetadata); // Optional: user metadata

  Sentry.withScope((scope) => {
    scope.setUser({
      email: email,
    });
    scope.setLevel("error"); // You can use "info", "warning", "fatal", etc.
    var response = {
      url: window.location.href,
      error: JSON.stringify(description),
      response: description?.response ? JSON.stringify(description?.response) : null,
      request: description?.request ? JSON.stringify(description?.request) : null,
    };
    // scope.setFingerprint([fingerprint]); // Optional: fingerprint for grouping
    scope.setExtra("error", error);
    scope.setExtra("more_details", response);
    Sentry.captureException(error);
  });
};

export const ErrorLogging = (error) => {
  const logError = () => {
    const email = localStorage.getItem("email");
    Sentry.withScope((scope) => {
      scope.setUser({
        email: email,
      });
      var response = {
        error: JSON.stringify(error),
        response: error?.response ? JSON.stringify(error?.response) : null,
        request: error?.request ? JSON.stringify(error?.request) : null,
      };
      scope.setExtra("error", error);
      scope.setExtra("more_details", response);
      Sentry.captureException(error);
    });
  };

  return logError();
};

export const openSmallScreenModal = (dispatch) => {
  dispatch(setSmallScreenModalOpen(true));
};
